import React, { useState } from 'react';
import { Tabs } from 'react-tabs';
import { Target, Upload, BarChart3, MessageSquare, History, Sparkles, TrendingUp } from 'lucide-react';
import JobMatchUpload from '../components/JobMatchUpload';
import MatchAnalysis from '../components/MatchAnalysis';
import InterviewPrep from '../components/InterviewPrep';
import InterviewStrategyCoach from '../components/InterviewStrategyCoach';
import SkillsGapAnalysis from '../components/SkillsGapAnalysis';
import MatchHistory from '../components/MatchHistory';
import JobMatchPrerequisitesChecker from '../components/JobMatchPrerequisitesChecker';
import { FeatureGate } from '../components/FeatureGate';
import { SetupFeeGate } from '../components/SetupFeeGate';
import { useAuth } from '../contexts/AuthContext';
import { useJobMatchPrerequisites } from '../hooks/useJobMatchPrerequisites';
import { FEATURE_GATES } from '../services/subscriptionService';

type TabView = 'upload' | 'analysis' | 'skills' | 'strategy' | 'interview' | 'history';

const JobMatchAI: React.FC = () => {
  const { user } = useAuth();
  const { canAccessJobMatch, isLoading: loadingPrerequisites } = useJobMatchPrerequisites();
  const [activeTab, setActiveTab] = useState<TabView>('upload');
  const [selectedMatchId, setSelectedMatchId] = useState<string | null>(null);

  const handleJobMatchCreated = (jobMatchId: string) => {
    setSelectedMatchId(jobMatchId);
    setActiveTab('analysis');
  };

  const handleSelectMatch = (jobMatchId: string) => {
    setSelectedMatchId(jobMatchId);
    setActiveTab('analysis');
  };

  const tabs = [
    {
      id: 'upload' as TabView,
      label: 'Upload Job',
      icon: <Upload className="h-4 w-4" />,
      description: 'Add a new job to analyze'
    },
    {
      id: 'history' as TabView,
      label: 'History',
      icon: <History className="h-4 w-4" />,
      description: 'View past job matches'
    },
    {
      id: 'analysis' as TabView,
      label: 'Analysis',
      icon: <BarChart3 className="h-4 w-4" />,
      description: 'View match analysis',
      disabled: !selectedMatchId
    },
    {
      id: 'skills' as TabView,
      label: 'Skills Gap',
      icon: <TrendingUp className="h-4 w-4" />,
      description: 'Development plan with ROI',
      disabled: !selectedMatchId
    },
    {
      id: 'strategy' as TabView,
      label: 'Interview Strategy',
      icon: <Sparkles className="h-4 w-4" />,
      description: 'Executive interview coaching',
      disabled: !selectedMatchId
    },
    {
      id: 'interview' as TabView,
      label: 'General Prep',
      icon: <MessageSquare className="h-4 w-4" />,
      description: 'General interview tips',
      disabled: !selectedMatchId
    }
  ];

  return (
    <SetupFeeGate featureName="Job Match AI">
      <FeatureGate
        featureKey={FEATURE_GATES.JOB_MATCH_AI}
        featureName="Job Match AI"
        showUpgradePrompt={true}
      >
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-primary rounded-lg">
              <Target className="h-6 w-6 text-white" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900">Job Match AI</h1>
          </div>
          <p className="text-gray-600">
            Analyze job opportunities and get personalized insights for your career journey
          </p>
        </div>

        <div className="bg-white rounded-xl border border-border/40 mb-6">
          <div className="border-b border-gray-200">
            <nav className="flex flex-wrap -mb-px">
              {tabs.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => !tab.disabled && setActiveTab(tab.id)}
                  disabled={tab.disabled}
                  className={`
                    flex items-center gap-2 px-6 py-4 border-b-2 font-medium text-sm
                    transition-colors
                    ${
                      activeTab === tab.id
                        ? 'border-primary text-primary'
                        : tab.disabled
                        ? 'border-transparent text-gray-400 cursor-not-allowed'
                        : 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300'
                    }
                  `}
                >
                  {tab.icon}
                  <span className="hidden sm:inline">{tab.label}</span>
                </button>
              ))}
            </nav>
          </div>
        </div>

        <div className="space-y-6">
          {!canAccessJobMatch && !loadingPrerequisites && (
            <JobMatchPrerequisitesChecker />
          )}

          {canAccessJobMatch && activeTab === 'upload' && (
            <JobMatchUpload onJobMatchCreated={handleJobMatchCreated} />
          )}

          {canAccessJobMatch && activeTab === 'history' && (
            <MatchHistory onSelectMatch={handleSelectMatch} />
          )}

          {canAccessJobMatch && activeTab === 'analysis' && selectedMatchId && (
            <MatchAnalysis
              jobMatchId={selectedMatchId}
              onAnalyze={() => {
                console.log('Analysis completed');
              }}
            />
          )}

          {canAccessJobMatch && activeTab === 'skills' && selectedMatchId && (
            <SkillsGapAnalysis jobMatchId={selectedMatchId} />
          )}

          {canAccessJobMatch && activeTab === 'strategy' && selectedMatchId && (
            <InterviewStrategyCoach jobMatchId={selectedMatchId} />
          )}

          {canAccessJobMatch && activeTab === 'interview' && selectedMatchId && (
            <InterviewPrep jobMatchId={selectedMatchId} />
          )}

          {(activeTab === 'analysis' || activeTab === 'skills' || activeTab === 'interview' || activeTab === 'strategy') && !selectedMatchId && (
            <div className="bg-white rounded-xl border border-border/40 p-12 text-center">
              <div className="max-w-sm mx-auto">
                <div className="bg-gray-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <Target className="h-8 w-8 text-gray-400" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  No Job Match Selected
                </h3>
                <p className="text-gray-600 mb-4">
                  Please select a job match from your history or upload a new one to view analysis.
                </p>
                <div className="flex gap-2 justify-center">
                  <button
                    onClick={() => setActiveTab('history')}
                    className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90 transition-colors"
                  >
                    View History
                  </button>
                  <button
                    onClick={() => setActiveTab('upload')}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
                  >
                    Upload New Job
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {selectedMatchId && (
          <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Target className="h-5 w-5 text-blue-600 mt-0.5" />
              <div className="flex-1">
                <h4 className="font-medium text-blue-900 mb-1">
                  Currently Viewing Job Match
                </h4>
                <p className="text-sm text-blue-700">
                  You're viewing analysis for a specific job. Use the tabs above to switch between different views.
                </p>
              </div>
              <button
                onClick={() => {
                  setSelectedMatchId(null);
                  setActiveTab('upload');
                }}
                className="text-blue-600 hover:text-blue-800 text-sm font-medium"
              >
                Clear Selection
              </button>
            </div>
          </div>
        )}
        </div>
      </div>
    </FeatureGate>
    </SetupFeeGate>
  );
};

export default JobMatchAI;
